import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { buildTemplateSelectItems, getSmartDefaultTemplate } from "@utils/helpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo, useState } from "react";

import { SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportTemplateReportTypeChoices,
  ReportTemplateStatus,
  ReportTemplateTypeEdge,
  useReportTemplatesQuery,
} from "@api/graphql/types-and-hooks";
import { useAdminData } from "@providers/AdminDataProvider";

export const useReportTemplateSelection = (
  reportType: ApiReportTemplateReportTypeChoices,
  queryKey: string,
  /**
   * Selected client. When set, the API narrows templates to the ones matching
   * that client's payor, falling back to every org template when the payor has
   * none of its own.
   */
  learnerId?: string | null,
) => {
  const { isUserEnterpriseAdmin } = useAdminData();
  const flags = useFlags<FeatureFlags>();
  const isDemoOrgMode = !!flags["demo-org-mode"];

  const templateStatuses = isUserEnterpriseAdmin
    ? [ReportTemplateStatus.Published, ReportTemplateStatus.Draft]
    : [ReportTemplateStatus.Published];

  const { data: templatesData, isLoading: templatesLoading } = useReportTemplatesQuery(
    { filter: { reportType, statuses: templateStatuses, learner: learnerId ?? undefined } },
    { queryKey: [queryKey, isUserEnterpriseAdmin, learnerId] },
  );

  const templateEdges = templatesData?.getReportTemplates?.edges;

  const templateSelections = useMemo(
    () => buildTemplateSelectItems((templateEdges ?? []) as ReportTemplateTypeEdge[], isDemoOrgMode),
    [templateEdges, isDemoOrgMode],
  );

  // The pick is stored together with the client it was made for. A different
  // client can mean a different payor and therefore a different template list,
  // so a stale pick is dropped rather than left pointing at a template that is
  // no longer offered. Deriving the selection instead of syncing it in an effect
  // keeps the smart default from needing a render pass to land.
  const [pick, setPick] = useState<{ learnerId: string | null; item?: SelectItem }>();
  const currentLearnerId = learnerId ?? null;

  const selectedTemplate =
    pick && pick.learnerId === currentLearnerId ? pick.item : getSmartDefaultTemplate(templateSelections) ?? undefined;

  const setSelectedTemplate = useCallback(
    (item?: SelectItem) => setPick({ learnerId: currentLearnerId, item }),
    [currentLearnerId],
  );

  return { templateSelections, templatesLoading, selectedTemplate, setSelectedTemplate };
};
